import { World } from "./world";
import { GameMap } from "./gamemap";
import { RNG } from "./rng";
import { Projectiles } from "./projectiles";
import { SpatialHash } from "./spatialhash";
import { FlowFieldManager } from "./pathfinding";
import { MAX_ENTITIES, DT, OWNER_PLAYER, OWNER_ENEMY } from "./constants";
import { DUEL_MAP, type Faction, type MapDef } from "../data";
import { initMatch } from "./entities";
import { applyCommands } from "./apply";
import { economyStep } from "./economy";
import { movementStep } from "./movement";
import { targetingStep, weaponsStep, projectilesStep, deathsStep } from "./combat";
import { Skirmisher, type Difficulty } from "./ai";
import type { Command } from "./commands";
import { getFaction } from "../data";
import { clamp } from "./math";

export interface FxEvent {
  type: "explosion" | "spark" | "muzzle";
  x: number;
  y: number;
  radius: number;
  faction: number;
  big?: boolean;
}

export interface SimConfig {
  playerFaction: Faction;
  enemyFaction: Faction;
  difficulty?: Difficulty;
  aiPlayers?: number[]; // owners controlled by AI (default: [OWNER_ENEMY])
  map?: MapDef;
}

export class Sim {
  readonly world: World;
  readonly rng: RNG;
  readonly proj = new Projectiles();
  readonly hash: SpatialHash;
  readonly flow: FlowFieldManager;

  buildPowerOn = new Float32Array(MAX_ENTITIES);
  reclaimPowerOn = new Float32Array(MAX_ENTITIES);
  // Build power applied to REPAIR a completed-but-damaged entity this tick
  // (energy-only heal; see economy.ts). Cleared and re-summed every tick, like
  // buildPowerOn, so it stays a pure function of the current orders.
  repairPowerOn = new Float32Array(MAX_ENTITIES);
  fxEvents: FxEvent[] = [];

  ais: Skirmisher[] = [];
  winner = 0; // 0 = ongoing, 1 = player wins, 2 = enemy wins
  playerCommander = -1;
  enemyCommander = -1;
  readonly cfg: SimConfig;

  constructor(seed: number, cfg: SimConfig) {
    this.cfg = cfg;
    const mapDef = cfg.map ?? DUEL_MAP;
    const gmap = new GameMap(mapDef);
    this.world = new World(gmap);
    this.rng = new RNG(seed);
    this.hash = new SpatialHash(gmap.worldW, gmap.worldH, 48);
    this.flow = new FlowFieldManager(gmap);

    const res = initMatch(this.world, { playerFaction: cfg.playerFaction, enemyFaction: cfg.enemyFaction });
    this.playerCommander = res.playerCommander;
    this.enemyCommander = res.enemyCommander;

    const diff = cfg.difficulty ?? "normal";
    const aiOwners = cfg.aiPlayers ?? [OWNER_ENEMY];
    const starts = gmap.def.starts;
    const posOf = (owner: number) => {
      const s = starts.find((s) => s.owner === owner)!;
      return { x: s.x, y: s.y };
    };
    for (const owner of aiOwners) {
      const enemy = owner === OWNER_PLAYER ? OWNER_ENEMY : OWNER_PLAYER;
      const fac = owner === OWNER_PLAYER ? cfg.playerFaction : cfg.enemyFaction;
      this.ais.push(new Skirmisher(owner, enemy, fac, posOf(owner), posOf(enemy), diff));
    }
  }

  factionColor(owner: number): string {
    const f = owner === OWNER_PLAYER ? this.cfg.playerFaction : this.cfg.enemyFaction;
    return getFaction(f).color;
  }

  private updateWind(): void {
    const d = this.world.gmap.def;
    const t = this.world.tick;
    const n = 0.5 + 0.34 * Math.sin(t * 0.006) + 0.16 * Math.sin(t * 0.017 + 1.3);
    this.world.windCurrent = d.windMin + (d.windMax - d.windMin) * clamp(n, 0, 1);
  }

  step(cmds: Command[]): void {
    if (this.winner !== 0) return;
    const w = this.world;

    // Snapshot previous positions for render interpolation.
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      w.prevX[e] = w.posX[e];
      w.prevY[e] = w.posY[e];
      w.prevHeading[e] = w.heading[e];
    }
    this.buildPowerOn.fill(0);
    this.reclaimPowerOn.fill(0);
    this.repairPowerOn.fill(0);

    this.updateWind();

    applyCommands(w, this, cmds);
    for (const ai of this.ais) ai.update(w, this);

    this.hash.rebuild(w);
    this.flow.updateObstacles(w); // refresh solid-obstacle overlay for pathing
    targetingStep(w, this);
    movementStep(w, this);

    economyStep(w, this);

    // Rebuild after movement so weapon/projectile collision uses fresh positions.
    this.hash.rebuild(w);
    weaponsStep(w, this);
    projectilesStep(w, this);
    deathsStep(w, this);

    // Decrement per-entity timers.
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e]) continue;
      if (w.reload[e] > 0) w.reload[e] = Math.max(0, w.reload[e] - DT);
      if (w.fireFlash[e] > 0) w.fireFlash[e] = Math.max(0, w.fireFlash[e] - DT);
    }

    this.checkVictory();
    w.tick++;
  }

  private commanderAlive(ent: number, owner: number): boolean {
    return (
      this.world.isAlive(ent) &&
      this.world.owner[ent] === owner &&
      this.world.def(ent).role === "commander"
    );
  }

  private checkVictory(): void {
    const enemyAlive = this.commanderAlive(this.enemyCommander, OWNER_ENEMY);
    const playerAlive = this.commanderAlive(this.playerCommander, OWNER_PLAYER);
    if (!enemyAlive) this.winner = 1;
    else if (!playerAlive) this.winner = 2;
  }
}
